import React, { useState, useEffect, useCallback, useRef } from "react"

import { useComponentizedPage } from "store/contexts/componentized-page"
import { currencyToCents, formatCurrency } from "utils/currency"
import useAddToCart from "utils/useAddToCart"

import {
  AddToCartButton,
  AffirmBadge,
  AllAccessButton,
  Badge,
  Badges,
  Container,
  UnlockDisclaimer,
} from "./styles"

const AffirmWidget = React.lazy(() => import("components/common/affirm"))

const ClassPurchaseForm = ({
  classId,
  classBundleId,
  hideUnlock,
  hideInstallmentPaymentOption,
  dataId,
  id,
}) => {
  const [affirmSelected, setAffirm] = useState(false)
  const cartRequest = useAddToCart()
  const { classesOnPage, classBundles } = useComponentizedPage()
  const cls = classesOnPage.find((c) => c.classId === classId)
  const classBundle = classBundles.find((bundle) => bundle.id === classBundleId)

  const widgetPrice = currencyToCents(cls?.price)
  const AffirmBadgeNeeded = parseInt(widgetPrice) >= 5000
  if (!cls && !classBundle) {
    return null
  }

  const onAddToCart = useCallback(() => {
    if (cls) {
      cartRequest.addClassToCart(
        classId,
        cls.title,
        cls.salePrice || cls.price,
        true,
        affirmSelected ? "affirm" : "one-time",
      )
    } else if (classBundle) {
      cartRequest.addClassToCart(
        classBundleId,
        classBundle.title,
        classBundle.classes.reduce((agg, c) => agg + c.price, 0) *
          (1 - classBundle.savingsPercentage),
        true,
        affirmSelected ? "affirm" : "one-time",
        true,
      )
    }
  }, [affirmSelected, cls, classBundle])

  const allAccessClass = classesOnPage.find(
    (c) => c.classId === Number(process.env.GATSBY_ALL_ACCESS_CLS_ID),
  )

  const onAllAccessClick = useCallback(() => {
    cartRequest.addClassToCart(
      allAccessClass.classId,
      allAccessClass.title,
      allAccessClass.price,
      true,
      affirmSelected ? "affirm" : "one-time",
    )
  }, [allAccessClass])

  const refAddToCartButton = useRef()
  const refAllAccessButton = useRef()
  useEffect(() => {
    if (refAddToCartButton.current && refAllAccessButton.current) {
      refAddToCartButton.current.style.width = `${refAllAccessButton.current.clientWidth}px`
    }
  }, [])

  return (
    <Container data-exp-id={dataId} noMargin={hideInstallmentPaymentOption} id={id}>
      {!hideInstallmentPaymentOption && AffirmBadgeNeeded && (
        <Badges>
          <AffirmBadge active={affirmSelected} onClick={() => setAffirm(true)}>
            <h5>pay as low as</h5>
            <React.Suspense>
              <AffirmWidget price={widgetPrice} />
            </React.Suspense>
          </AffirmBadge>
          <Badge active={!affirmSelected} onClick={() => setAffirm(false)}>
            <h5>1 payment</h5>
            <p>{formatCurrency(cls.salePrice || cls.price, true)}</p>
          </Badge>
        </Badges>
      )}
      <AddToCartButton
        ref={refAddToCartButton}
        type="button"
        onClick={onAddToCart}
        disabled={Boolean(cartRequest.addingToCart)}
      >
        {cartRequest.addingToCart ? "Adding to cart..." : "Add to cart"}
      </AddToCartButton>
      {!hideUnlock && allAccessClass && (
        <AllAccessButton ref={refAllAccessButton} type="button" onClick={onAllAccessClick}>
          unlock all mbg classes for {formatCurrency(allAccessClass.price, true)}
        </AllAccessButton>
      )}
      {!hideUnlock && allAccessClass && (
        <UnlockDisclaimer>
          Limited time offer:&nbsp;
          <a href="/classes/all-access" title="All access">
            learn more about unlock
          </a>
        </UnlockDisclaimer>
      )}
    </Container>
  )
}

export default ClassPurchaseForm
